'use client'

import { ArrowLeft24, X24 } from '@vinted/monochrome-icons'
import { Button, Cell, Dialog, Divider, InfoBanner, Navigation, Spacer, Text } from '@vinted/web-ui'
import { useContext, useEffect, useState, useRef } from 'react'

import {
  GetShippingLabelContext,
  useDropOffPointMapContext,
} from '@marketplace-web/shipping-services-provider/shipping-services-provider-feature'
import {
  useConversationContext,
  useConversationSidebarContext,
} from '@marketplace-web/messaging/conversation-providers-feature'
import { useAbTest, useTrackAbTest } from '@marketplace-web/feature-flags/ab-tests-data'
import { ErrorItem, useFetch } from '@marketplace-web/shared/api-client'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { UiState } from '@marketplace-web/shared/ui-state'
import {
  GenerateLabelModal,
  DropOffTypeKey,
  DropOffsPointType,
  ShipmentEntryType,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'
import { TransactionSide } from '@marketplace-web/messaging/conversation-messaging-data'
import { useLoadGoogleApi } from '@marketplace-web/shipping/google-maps-feature'
import {
  getDefaultShippingAddress,
  transformDefaultShippingAddress,
} from '@marketplace-web/address/address-data'

import { clickEvent, viewEvent } from '_libs/common/event-tracker/events'
import {
  FieldName as ShippingContactFieldName,
  PhoneRequirement,
  ShippingContactType,
} from '@marketplace-web/shipping/shipping-contact-data'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'

import BuyerAddress from './Components/BuyerAddress/BuyerAddress'
import Carrier from './Components/Carrier/Carrier'
import DropOffPoints from './Components/DropOffPoints/DropOffPoints'
import PickupDate from './Components/PickupDate/PickupDate'
import SellerAddress from './Components/SellerAddress/SellerAddress'
import GenerateShippingLabelShippingContact from './Components/GenerateShippingLabelShippingContact'
import ShippingMethod from './Components/ShippingMethod/ShippingMethod'
import { isDateObjectPresent } from './utils'
import {
  incrementScreenLoadCounter,
  observePageLoadTime,
} from '../utils/generate-label-observability'

type Props = {
  isShippingLabelLoading: boolean
  isGetShippingLabelModalOpen: boolean
  onGenerateLabelClick: () => void
}

const GenerateShippingLabelModal = ({
  isShippingLabelLoading,
  isGetShippingLabelModalOpen,
  onGenerateLabelClick,
}: Props) => {
  const pageLoadStartTime = useRef<number>(performance.now())
  const isLoadTimeObserved = useRef(false)

  const translate = useTranslate('conversation.get_shipping_label.modal')
  const translateShippingContact = useTranslate('shipping_contact')
  const { track } = useTracking()
  const { conversation } = useConversationContext()
  const { openSidebar } = useConversationSidebarContext()

  const {
    fetch: fetchDefaultShippingAddress,
    transformedData: defaultShippingAddress,
    isLoading: isDefaultShippingAddressLoading,
  } = useFetch(getDefaultShippingAddress, transformDefaultShippingAddress)

  const transactionId = conversation?.transaction?.id
  const userSide = conversation?.transaction?.userSide
  const shipmentId = conversation?.transaction?.shipment?.id

  const footnoteShippingLabelGenerationAbTest = useAbTest('dd_footnote_shipping_label_generation')

  const {
    state: {
      collectionDates: { collectionDates, confirmedCollectionDate, clearSelectedCollection },
      dropOffTypeSelection: { clearDropOffTypeSelection, selectedDropOffTypeKey },
      labelTypeSelection: { clearLabelTypeSelection },
      activeModal: { setActiveModal },
      labelOptions: { fetchLabelOptions, labelOptions, labelOptionsUiState },
      labelTypeSelection: { selectedLabelType },
      dropOffPointTypeSelection: { handleClearDropOffPointTypeSelection, selectedDropOffPointType },
    },
  } = useContext(GetShippingLabelContext)
  const { fetchNearbyDropOffPoints, clearNearbyDropOffPoints, selectedDropOffPoint } =
    useDropOffPointMapContext()

  const [pickUpValidation, setPickUpValidation] = useState<string>('')
  const [addressValidation, setAddressValidation] = useState<string>('')
  const [shippingContactFieldError, setShippingContactFieldError] = useState<ErrorItem | null>(null)

  const isGoogleApiLoaded = useLoadGoogleApi()
  const isDropOffMapEnabled = labelOptions?.dropOffMapEnabled
  const labelOptionsDropOffs = labelOptions?.dropOffs
  const isHomePickupSelected = selectedDropOffPointType === DropOffsPointType.Home
  const isPickupDateEnabled = labelOptions?.dropOffTypes?.[0]?.pickUpDateShown
  const isPickpDateFlow =
    isPickupDateEnabled ||
    isHomePickupSelected ||
    (labelOptionsDropOffs &&
      labelOptionsDropOffs.length === 1 &&
      labelOptionsDropOffs[0]?.type === DropOffsPointType.Home)
  const isReturnShipment = labelOptions?.shipmentEntryType === ShipmentEntryType.Return
  const isShippingContactDisabled =
    !labelOptions?.sender.phoneNumberRequirement ||
    labelOptions?.sender.phoneNumberRequirement === PhoneRequirement.Disabled
  const termsAndConditionsText = labelOptions?.carrier?.termsAndConditions
  const isPackageTypeLabelTypeFlow =
    labelOptions?.availableLabelTypes &&
    labelOptions?.availableLabelTypes.length > 1 &&
    labelOptions?.dropOffTypes &&
    labelOptions?.dropOffTypes.length > 1
  const isDropOffPointTypeFlow = labelOptions?.dropOffs && labelOptions?.dropOffs.length > 1

  useEffect(() => {
    if (isLoadTimeObserved.current) return

    const observeLoadEnd = (state: 'succeeded' | 'failed') => {
      const itemLoadTime = performance.now() - pageLoadStartTime.current

      observePageLoadTime(state, itemLoadTime)
      incrementScreenLoadCounter(state)

      isLoadTimeObserved.current = true
    }

    if (labelOptions) {
      observeLoadEnd('succeeded')
    } else {
      observeLoadEnd('failed')
    }
  }, [labelOptions])

  useEffect(() => {
    if (!isGetShippingLabelModalOpen) return
    if (!labelOptions?.sender.phoneNumber) return

    setShippingContactFieldError(null)
  }, [labelOptions?.sender.phoneNumber, isGetShippingLabelModalOpen])

  useEffect(() => {
    if (!isGoogleApiLoaded || !defaultShippingAddress) return
    if (!isDropOffMapEnabled) return

    if (isGetShippingLabelModalOpen)
      fetchNearbyDropOffPoints({ address: defaultShippingAddress, selectedLabelType })
  }, [
    fetchNearbyDropOffPoints,
    isDropOffMapEnabled,
    defaultShippingAddress,
    isGetShippingLabelModalOpen,
    isGoogleApiLoaded,
    selectedLabelType,
  ])

  useEffect(() => {
    if (isGetShippingLabelModalOpen) fetchDefaultShippingAddress()
  }, [fetchDefaultShippingAddress, isGetShippingLabelModalOpen])

  useEffect(() => {
    if (shipmentId && isGetShippingLabelModalOpen) fetchLabelOptions({ shipmentId })
  }, [shipmentId, isGetShippingLabelModalOpen, fetchLabelOptions])

  useEffect(() => {
    if (!isGetShippingLabelModalOpen || !transactionId) return

    if (!isDropOffMapEnabled) return
    if (!selectedDropOffPoint) return

    const { distance, distanceUnit } = selectedDropOffPoint

    const targetDetails = JSON.stringify({
      transaction_id: transactionId,
      distance,
      distance_unit: distanceUnit,
    })

    track(
      viewEvent({
        screen: Screen.GetShippingLabel,
        target: 'see_drop_off_points',
        targetDetails,
      }),
    )
  }, [
    track,
    transactionId,
    isGetShippingLabelModalOpen,
    isDropOffMapEnabled,
    selectedDropOffPoint,
    // if user changes address distance chnages, need to track again
    defaultShippingAddress,
  ])

  useTrackAbTest(
    footnoteShippingLabelGenerationAbTest,
    isGetShippingLabelModalOpen && !termsAndConditionsText,
  )

  function trackGenerateLabelClick() {
    let dropOffTypeKey

    if (selectedDropOffPointType) {
      dropOffTypeKey = selectedDropOffPointType === DropOffsPointType.Home ? 'home' : 'shop'
    }

    if (!dropOffTypeKey && selectedDropOffTypeKey) {
      dropOffTypeKey = selectedDropOffTypeKey
    }

    const targetDetails = {
      transaction_id: transactionId,
      dropoff_type_key: dropOffTypeKey,
    }

    track(
      clickEvent({
        target: ClickableElement.Confirm,
        screen: Screen.GetShippingLabel,
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  function trackChooseDropOffPointClick() {
    const targetDetails = {
      transaction_id: transactionId,
    }

    track(
      clickEvent({
        target: ClickableElement.SeeDropOffPoints,
        screen: Screen.GetShippingLabel,
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  function isDropOffFlowEnabled() {
    return labelOptions?.dropOffTypes && labelOptions?.dropOffTypes.length > 1
  }

  function isReusablePackagingFlowEnabled() {
    return labelOptions?.dropOffTypes?.some(
      ({ key }) => key === DropOffTypeKey.ReusablePackage || key === DropOffTypeKey.MyOwnPackage,
    )
  }

  function getSubmitNote() {
    if (footnoteShippingLabelGenerationAbTest?.variant === 'on')
      return translate('generate_label_footnote')

    return translate('generate_label_note')
  }

  function isSelectedCollectionDatesValid() {
    let isValid = true

    if (!isPickpDateFlow) return isValid

    const isDateAvailable = isDateObjectPresent(confirmedCollectionDate, collectionDates?.dates)

    if (!isDateAvailable) {
      clearSelectedCollection()

      setPickUpValidation(translate('drop_off_points.drop_off_date_validation'))

      isValid = false
    }

    return isValid
  }

  function handleValidation() {
    let isValid = true

    if (!defaultShippingAddress) {
      setAddressValidation(translate('address_field_validation'))

      isValid = false
    }

    if (isPickpDateFlow && !confirmedCollectionDate) {
      setPickUpValidation(translate('choose_drop_off_date.validation'))

      isValid = false
    }

    return isValid
  }

  function getIsShippingContactValid() {
    if (!userSide) return false

    const phoneNumber = labelOptions?.sender.phoneNumber
    const phoneRequirement = labelOptions?.sender.phoneNumberRequirement
    const isShippingContactCompleted = !(
      phoneRequirement === PhoneRequirement.Mandatory && !phoneNumber
    )

    if (isShippingContactDisabled) return true
    if (isShippingContactCompleted) return true

    setShippingContactFieldError({
      field: ShippingContactFieldName.ShippingContact,
      value: translateShippingContact(`${userSide}.validation.required`),
    })

    return false
  }

  function handleConfirmationOpen(event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) {
    if (isShippingLabelLoading) {
      event.preventDefault()

      return
    }

    if (!handleValidation()) return

    setActiveModal(GenerateLabelModal.ConfirmationModal)
  }

  function handleGenerateLabelClick(
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) {
    if (isShippingLabelLoading) {
      event.preventDefault()

      return
    }

    const isHandleValidationValid = handleValidation()
    const isShippingContactValid = getIsShippingContactValid()

    if (!isHandleValidationValid || !isShippingContactValid) return

    if (!isSelectedCollectionDatesValid()) return

    trackGenerateLabelClick()
    onGenerateLabelClick()
  }

  function removePickUpError() {
    setPickUpValidation('')
  }

  function handleShippingContactModalOpen() {
    setActiveModal(GenerateLabelModal.ShippingContactModal)
  }

  function handleModalClose() {
    clearDropOffTypeSelection()

    if (confirmedCollectionDate) clearSelectedCollection()
    if (isDropOffPointTypeFlow) handleClearDropOffPointTypeSelection()
    removePickUpError()
    setAddressValidation('')
    setShippingContactFieldError(null)

    clearNearbyDropOffPoints()

    clearLabelTypeSelection()
    setActiveModal(GenerateLabelModal.None)
  }

  function handleReturnShipmentPreviousClick() {
    if (isDropOffPointTypeFlow) {
      return setActiveModal(GenerateLabelModal.DropOffPointTypeSelectionModal)
    }

    if (isPackageTypeLabelTypeFlow) {
      return setActiveModal(GenerateLabelModal.DigitalLabelTypeSelectionModal)
    }

    if (labelOptions?.dropOffTypes?.length && !isPickupDateEnabled) {
      return setActiveModal(GenerateLabelModal.DropOffTypeSelectionModal)
    }

    if (labelOptions?.availableLabelTypes?.length) {
      return setActiveModal(GenerateLabelModal.DigitalLabelTypeSelectionModal)
    }

    return setActiveModal(GenerateLabelModal.ReturnTheOrder)
  }

  function handlePreviousClick() {
    if (isDropOffPointTypeFlow) {
      return setActiveModal(GenerateLabelModal.DropOffPointTypeSelectionModal)
    }

    if (isReturnShipment) {
      return handleReturnShipmentPreviousClick()
    }

    if (isPackageTypeLabelTypeFlow) {
      return setActiveModal(GenerateLabelModal.DropOffTypeSelectionModal)
    }

    if (labelOptions?.dropOffTypes?.length && !isPickupDateEnabled) {
      return setActiveModal(GenerateLabelModal.DropOffTypeSelectionModal)
    }

    return setActiveModal(GenerateLabelModal.DigitalLabelTypeSelectionModal)
  }

  function handleAddressDetailsClick() {
    setAddressValidation('')
    setPickUpValidation('')

    setActiveModal(GenerateLabelModal.AddressModal)
  }

  function handleChoosePickUpDateOpen() {
    removePickUpError()

    setActiveModal(GenerateLabelModal.CourierPickUpDateSelectionModal)
  }

  function handleChooseDropOffPointOpen() {
    trackChooseDropOffPointClick()

    setActiveModal(GenerateLabelModal.DropOffPointMapModal)
  }

  function renderNavigationBackArrow() {
    const showBackButton =
      isReturnShipment ||
      labelOptions?.availableLabelTypes?.length ||
      isDropOffFlowEnabled() ||
      isReusablePackagingFlowEnabled() ||
      isDropOffPointTypeFlow

    if (showBackButton)
      return (
        <Button
          styling={Button.Styling.Flat}
          iconName={ArrowLeft24}
          inline
          onClick={handlePreviousClick}
          testId="generate-label-modal-previous"
        />
      )

    return null
  }

  function renderSellerAddress() {
    if (isDefaultShippingAddressLoading)
      return <ContentLoader testId="generate-shipping-address-loader" />

    return (
      <SellerAddress
        address={defaultShippingAddress || null}
        onAddressDetailsClick={handleAddressDetailsClick}
        error={addressValidation}
      />
    )
  }

  function renderShippingContact(type: ShippingContactType) {
    return (
      <GenerateShippingLabelShippingContact
        transactionId={transactionId}
        userSide={userSide}
        error={shippingContactFieldError}
        type={type}
        onClick={handleShippingContactModalOpen}
      />
    )
  }

  function renderGenerateShippingLabelModalNavigation() {
    return (
      <>
        <Navigation
          body={labelOptions?.screenTitle}
          left={renderNavigationBackArrow()}
          right={
            <Button
              styling={Button.Styling.Flat}
              onClick={handleModalClose}
              iconName={X24}
              inline
              testId="generate-label-modal-close"
            />
          }
        />
        <Divider />
      </>
    )
  }

  function trackCangeDeliveryOptionClick() {
    const targetDetails = {
      transaction_id: transactionId,
    }

    track(
      clickEvent({
        target: ClickableElement.RequestCarrierChange,
        screen: Screen.GetShippingLabel,
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  function handleInfoBannerLinkClick(event: React.MouseEvent<Element, MouseEvent>) {
    event.preventDefault()

    setActiveModal(GenerateLabelModal.None)
    openSidebar()
  }

  function handleCangeDeliveryOptionClick(event: React.MouseEvent<Element, MouseEvent>) {
    event.preventDefault()

    trackCangeDeliveryOptionClick()

    setActiveModal(GenerateLabelModal.CarrierChangeModal)
  }

  const handleClick = (event: React.MouseEvent) => {
    const targetElement = event.target as HTMLElement

    if (targetElement.classList.contains('react-change-delivery-option-link')) {
      handleCangeDeliveryOptionClick(event)
    }
    if (targetElement.classList.contains('react-order-details-link')) {
      handleInfoBannerLinkClick(event)
    }
  }

  const getInfoBannerType = () =>
    labelOptions?.infoBannerType === 'info' ? InfoBanner.Type.Info : InfoBanner.Type.Warning

  function renderInfoBanner() {
    if (!labelOptions?.infoBannerText) return null

    return (
      <InfoBanner
        body={
          <span role="none" onClick={handleClick}>
            <Text
              text={labelOptions.infoBannerText}
              html
              testId="generate-label-modal-info-banner-text"
              as="span"
            />
          </span>
        }
        type={getInfoBannerType()}
      />
    )
  }

  function renderSubmitNote() {
    return (
      <Text
        as="h4"
        type={Text.Type.Caption}
        text={termsAndConditionsText || getSubmitNote()}
        html={!!termsAndConditionsText}
      />
    )
  }

  function renderGenerateLabelSubmit() {
    const buttonText = labelOptions?.actionTitle

    return (
      <Cell>
        <Cell styling={Cell.Styling.Tight}>
          {renderSubmitNote()}
          <Spacer size={Spacer.Size.Large} />
          {isDropOffFlowEnabled() && !isReusablePackagingFlowEnabled() ? (
            <>
              <Button
                styling={Button.Styling.Filled}
                text={buttonText}
                onClick={handleConfirmationOpen}
                testId="get-label-modal-confirm"
                isLoading={isShippingLabelLoading}
                disabled={isShippingLabelLoading}
              />
              <Spacer />
              <Button
                styling={Button.Styling.Flat}
                text={translate('actions.cancel')}
                onClick={handleModalClose}
                testId="get-label-modal-cancel"
              />
            </>
          ) : (
            <Button
              styling={Button.Styling.Filled}
              text={buttonText}
              onClick={handleGenerateLabelClick}
              testId="get-label-modal-generate"
              isLoading={isShippingLabelLoading}
              disabled={isShippingLabelLoading}
            />
          )}
        </Cell>
      </Cell>
    )
  }

  function renderModalContent() {
    if (labelOptionsUiState === UiState.Pending)
      return <ContentLoader testId="generate-shipping-label-loader" />

    const isSeller = userSide === TransactionSide.Seller

    return (
      <ScrollableArea>
        {renderGenerateShippingLabelModalNavigation()}
        {renderInfoBanner()}
        <Carrier userSide={userSide} />
        <ShippingMethod />
        <DropOffPoints
          onDropOffPointOpen={handleChooseDropOffPointOpen}
          address={defaultShippingAddress}
        />
        {renderSellerAddress()}
        <PickupDate
          address={defaultShippingAddress || null}
          pickUpValidation={pickUpValidation}
          onClick={handleChoosePickUpDateOpen}
        />
        {renderShippingContact(
          isSeller ? ShippingContactType.DigitalLabel : ShippingContactType.ReturnLabel,
        )}
        <BuyerAddress />
        {renderGenerateLabelSubmit()}
      </ScrollableArea>
    )
  }

  function renderGenerateShippingLabelModal() {
    return (
      <Dialog
        hasScrollableContent
        show={isGetShippingLabelModalOpen}
        className="u-flexbox"
        testId="generate-shipping-label-modal"
      >
        {renderModalContent()}
      </Dialog>
    )
  }

  return renderGenerateShippingLabelModal()
}

export default GenerateShippingLabelModal
